/**
 * notes-service.js
 * Orchestration layer for the Notes module.
 *
 * Architecture:
 *   UI → notes-service → notesStorage (Dexie) + notesSync (Supabase)
 *
 * Writes always go to local first (optimistic, instant feedback).
 * Supabase is updated asynchronously via notesSync.queueSync.
 *
 * Load flow (stale-while-revalidate):
 *   1. Load from Dexie immediately
 *   2. Fetch from Supabase in background (if online)
 *   3. Merge: local dirty notes take priority
 *   4. Remove server-deleted notes from local cache
 */

import { generateUUID } from './uuid';
import { notesStorage } from './notes-storage';
import * as notesApi from './notes-api';
import { queueSync, syncNotes } from './notes-sync';

/** Sort by last local edit → last remote update → oldest first. */
function sortByUpdated(a, b) {
  const ta = a.updated_locally_at || a.updated_at || a.created_at || '';
  const tb = b.updated_locally_at || b.updated_at || b.created_at || '';
  return tb.localeCompare(ta); // newest first
}

/**
 * Load all notes for a district.
 * Tries local cache first, then revalidates from Supabase.
 * @param {string} districtId
 * @returns {Promise<Object[]>} merged, visible notes (excludes pending-delete)
 */
export async function loadNotes(districtId, { isCoordinator = false } = {}) {
  const localNotes = await notesStorage.getAll(districtId);
  let visible = localNotes.filter((n) => !n._deleted).sort(sortByUpdated);

  // Coordinators must never see notes marked as personal (pastor-private).
  // Apply the same filter to the local cache for offline consistency.
  if (isCoordinator) {
    visible = visible.filter((n) => n.visibility_type !== 'personal');
  }

  if (typeof navigator !== 'undefined' && !navigator.onLine) {
    return visible;
  }

  try {
    const remoteNotes = await notesApi.fetchNotes(districtId, { isCoordinator });

    const localMap = new Map(localNotes.map((n) => [n.id, n]));
    const remoteMap = new Map(remoteNotes.map((n) => [n.id, n]));

    // Save remote notes to local — skip ones we have dirty local changes for
    const saves = [];
    for (const remote of remoteNotes) {
      const local = localMap.get(remote.id);
      if (!local || !local.dirty) {
        const merged = {
          // Preserve local metadata not stored on server
          last_viewed_at: local?.last_viewed_at ?? null,
          // Remote wins for all content fields
          ...remote,
          // Normalise new fields for old rows that may not have them yet
          note_type:       remote.note_type       ?? 'text',
          checklist_items: remote.checklist_items ?? [],
          linked_type:  remote.linked_type  ?? 'general',
          linked_id:    remote.linked_id    ?? null,
          linked_label: remote.linked_label ?? null,
          category:        remote.category         ?? 'general',
          visibility:      remote.visibility       ?? 'private',
          visibility_type: remote.visibility_type  ?? 'district',
          is_reference: remote.is_reference ?? false,
          // Sync state
          dirty: false,
          sync_status: 'synced',
          _local_only: false,
          _deleted: false,
        };
        saves.push(notesStorage.save(merged));
        localMap.set(remote.id, merged);
      }
    }

    // Remote-delete: notes present locally (clean, non-dirty) but gone from server
    for (const [id, local] of localMap) {
      if (!local.dirty && !local._local_only && !remoteMap.has(id)) {
        saves.push(notesStorage.delete(id));
        localMap.delete(id);
      }
    }

    await Promise.all(saves);

    // Flush any dirty notes we picked up during merge
    queueSync(districtId, 0);

    return Array.from(localMap.values())
      .filter((n) => !n._deleted)
      .sort(sortByUpdated);
  } catch {
    // Network failure — serve local cache
    return visible;
  }
}

/**
 * Build a fresh local note object (not yet persisted).
 *
 * linked_type values: 'general' | 'person' | 'family' | 'church' | 'event' | 'preaching'
 * category values:    'general' | 'espiritual' | 'pastoral' | 'administrativo' | 'personal'
 * visibility values:  'private' | 'district'
 *
 * @param {string} districtId
 * @returns {Object}
 */
export function buildLocalNote(districtId) {
  const now = new Date().toISOString();
  return {
    id: generateUUID(),
    district_id: districtId,
    title: '',
    content: '',
    tags: [],
    // note format
    note_type: 'text',
    checklist_items: [],
    // entity linking
    linked_type: 'general',
    linked_id: null,
    linked_label: null,       // denormalized for display & offline search
    // classification
    category:        'general',
    visibility:      'private',
    visibility_type: 'district',
    is_reference: false,      // important/frequent-consult note (replaces 'pinned')
    // legacy compat — kept for old stored notes
    pinned: false,
    archived: false,
    // sync state
    dirty: true,
    _local_only: true,
    _deleted: false,
    sync_status: 'pending',
    // timestamps
    created_at: now,
    updated_at: now,
    updated_locally_at: now,
    last_viewed_at: null,
  };
}

/**
 * Persist a new note locally and schedule Supabase sync.
 * Returns the note with the stable local ID (caller can navigate to it immediately).
 * @param {Object} data — partial note fields
 * @param {string} districtId
 * @returns {Promise<Object>}
 */
export async function createNote(data, districtId) {
  const now = new Date().toISOString();
  const note = {
    ...buildLocalNote(districtId),
    ...data,
    updated_locally_at: now,
  };
  await notesStorage.save(note);
  queueSync(districtId);
  return note;
}

/**
 * Apply partial updates to a note locally, then schedule sync.
 * @param {string} id
 * @param {Object} updates
 * @param {string} districtId
 * @returns {Promise<Object>} updated note
 */
export async function updateNote(id, updates, districtId) {
  const existing = await notesStorage.getById(id);
  if (!existing) throw new Error(`Note not found: ${id}`);

  const now = new Date().toISOString();
  const updated = {
    ...existing,
    ...updates,
    dirty: true,
    sync_status: 'pending',
    updated_locally_at: now,
  };

  await notesStorage.save(updated);
  queueSync(districtId);
  return updated;
}

/**
 * Soft-delete a note locally, then sync the deletion.
 * If the note was never synced (_local_only), removes it entirely.
 * @param {string} id
 * @param {string} districtId
 */
export async function deleteNote(id, districtId) {
  const existing = await notesStorage.getById(id);
  if (!existing) return;

  if (existing._local_only) {
    // Never reached Supabase — just delete locally
    await notesStorage.delete(id);
  } else {
    // Mark for remote deletion
    await notesStorage.save({
      ...existing,
      dirty: true,
      _deleted: true,
      sync_status: 'pending',
      updated_locally_at: new Date().toISOString(),
    });
    // Flush immediately so the delete reaches Supabase as soon as possible
    queueSync(districtId, 500);
  }
}

/**
 * Force a sync of all dirty notes (e.g. on app focus, on online event).
 * @param {string} districtId
 */
export async function flushSync(districtId) {
  await syncNotes(districtId);
}
